import type { LeaderDetailDecisionPresentation } from "@/lib/coach/leaderDetailDecisionPresentation";

export type CoachLeaderDetailContextSourceType =
  | "meeting"
  | "transcript"
  | "reflection"
  | "assessment"
  | "artifact"
  | "practice_focus";

export interface CoachLeaderDetailRecentContextFact {
  readonly canonicalSourceId: string;
  readonly sourceType: CoachLeaderDetailContextSourceType;
  readonly occurredAt: string;
  readonly title: string;
  readonly summary: string;
  readonly usable: boolean;
  readonly occurredAtLabel?: "Meeting" | "Transcript added" | "Reflection added" | "Artifact added";
  readonly evidenceReference: {
    readonly evidenceId: string;
    readonly link: string | null;
  };
}

export interface CoachLeaderDetailUpcomingSessionFact {
  readonly canonicalSessionId: string;
  readonly startsAt: string;
  readonly title: string;
}

export type CoachLeaderDetailRelationshipFact =
  | { readonly status: "checking" }
  | { readonly status: "available" }
  | { readonly status: "revoked" }
  | { readonly status: "retryable_error" };

export type CoachLeaderDetailSourceReadinessFact =
  | { readonly status: "loading" }
  | {
      readonly status: "ready";
      readonly unavailableSections: readonly string[];
    }
  | {
      readonly status: "partial";
      readonly unavailableSections: readonly string[];
    }
  | {
      readonly status: "retryable_error";
      readonly unavailableSections: readonly string[];
    };

export type CoachLeaderDetailBillingFact =
  | { readonly status: "available" }
  | { readonly status: "expired_trial" }
  | { readonly status: "pending_transfer" }
  | { readonly status: "unknown" };

export interface CoachLeaderDetailPresentationInput {
  readonly relationship: CoachLeaderDetailRelationshipFact;
  readonly sourceReadiness?: CoachLeaderDetailSourceReadinessFact;
  readonly billing: CoachLeaderDetailBillingFact;
  readonly upcomingSessionReadiness?:
    | { readonly status: "loading" }
    | { readonly status: "ready" }
    | { readonly status: "retryable_error" };
  readonly reviewReadyEvidence: CoachLeaderDetailRecentContextFact | null;
  readonly upcomingSession: CoachLeaderDetailUpcomingSessionFact | null;
  readonly recentContext: readonly CoachLeaderDetailRecentContextFact[];
}

export type CoachLeaderDetailPrimaryAction =
  | {
      readonly kind: "back_to_leaders";
      readonly label: "Back to Leaders";
    }
  | {
      readonly kind: "retry";
      readonly label: "Try again";
    }
  | {
      readonly kind: "view_plans";
      readonly label: "View plans";
      readonly reason: "trial_expired";
    }
  | {
      readonly kind: "resolve_billing_transfer";
      readonly label: "Resolve billing";
    }
  | {
      readonly kind: "review_evidence";
      readonly label: "Review evidence";
      readonly route: string;
      readonly source: {
        readonly canonicalSourceId: string;
        readonly sourceType: CoachLeaderDetailContextSourceType;
        readonly occurredAt: string;
        readonly title: string;
      };
    }
  | {
      readonly kind: "prepare_upcoming_session";
      readonly label: "Prepare for next session";
      readonly route: string;
      readonly session: CoachLeaderDetailUpcomingSessionFact;
    }
  | {
      readonly kind: "follow_decision";
      readonly label: string;
      readonly route: string;
    }
  | {
      readonly kind: "add_meeting_or_transcript";
      readonly label: "Add a meeting or transcript";
    };

export type CoachLeaderDetailPriority =
  | {
      readonly kind: "relationship_unavailable";
      readonly title: "Leader access is unavailable";
      readonly reason: "The current coaching relationship could not be confirmed.";
    }
  | {
      readonly kind: "retry_required";
      readonly title: "Some coaching context is unavailable";
      readonly reason: string;
    }
  | {
      readonly kind: "full_access_required";
      readonly title: "Full access is required for new preparation";
      readonly reason: "Existing coaching history remains available. A plan is required for new intelligence.";
    }
  | {
      readonly kind: "billing_transfer_pending";
      readonly title: "Choose who will manage billing";
      readonly reason: "A billing transfer is waiting for your decision for this leader.";
    }
  | {
      readonly kind: "review_ready";
      readonly title: "Review the latest coaching evidence";
      readonly reason: string;
      readonly source: {
        readonly canonicalSourceId: string;
        readonly sourceType: CoachLeaderDetailContextSourceType;
        readonly occurredAt: string;
        readonly title: string;
      };
    }
  | {
      readonly kind: "upcoming_session";
      readonly title: string;
      readonly reason: string;
      readonly session: CoachLeaderDetailUpcomingSessionFact;
    }
  | {
      readonly kind: "add_context";
      readonly title: "Add current coaching context";
      readonly reason: "No usable meeting or transcript context is available yet.";
    }
  | {
      readonly kind: "decision_action";
      readonly title: string;
      readonly reason: string;
    };

interface CoachLeaderDetailPresentationBase {
  readonly priority: CoachLeaderDetailPriority | null;
  readonly primaryAction: CoachLeaderDetailPrimaryAction | null;
  readonly recentContext: readonly CoachLeaderDetailRecentContextFact[];
  readonly unavailableSections: readonly string[];
}

export type CoachLeaderDetailPresentation =
  | (CoachLeaderDetailPresentationBase & {
      readonly status: "loading";
      readonly scope: "relationship" | "leader_detail";
    })
  | (CoachLeaderDetailPresentationBase & {
      readonly status: "relationship_unavailable";
    })
  | (CoachLeaderDetailPresentationBase & {
      readonly status: "retryable_error";
    })
  | (CoachLeaderDetailPresentationBase & {
      readonly status: "partial";
    })
  | (CoachLeaderDetailPresentationBase & {
      readonly status: "ready";
    });

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const parseRfc3339Instant = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(Z|[+-](\d{2}):(\d{2}))$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] ? Number(match[8]) : 0;
  const offsetMinute = match[9] ? Number(match[9]) : 0;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (year < 1000
    || month < 1 || month > 12
    || day < 1 || day > daysInMonth
    || hour > 23 || minute > 59 || second > 59
    || offsetHour > 23 || offsetMinute > 59) {
    return null;
  }

  const instant = Date.parse(value);
  return Number.isFinite(instant) ? instant : null;
};

const isOccurredAt = (value: string) => parseRfc3339Instant(value) !== null;

function hasValidContextMetadata(
  fact: unknown,
): fact is CoachLeaderDetailRecentContextFact {
  if (typeof fact !== "object" || fact === null) return false;

  const candidate = fact as Partial<CoachLeaderDetailRecentContextFact>;
  return isNonEmptyString(candidate.canonicalSourceId)
    && isNonEmptyString(candidate.title)
    && isNonEmptyString(candidate.summary)
    && isNonEmptyString(candidate.occurredAt)
    && isOccurredAt(candidate.occurredAt)
    && typeof candidate.usable === "boolean"
    && typeof candidate.evidenceReference === "object"
    && candidate.evidenceReference !== null
    && isNonEmptyString(candidate.evidenceReference.evidenceId)
    && (candidate.evidenceReference.link === null || typeof candidate.evidenceReference.link === "string")
    && [
      "meeting",
      "transcript",
      "reflection",
      "assessment",
      "artifact",
      "practice_focus",
    ].includes(candidate.sourceType as CoachLeaderDetailContextSourceType);
}

type CoachLeaderDetailDecision = {
  readonly priority: CoachLeaderDetailPriority | null;
  readonly primaryAction: CoachLeaderDetailPrimaryAction | null;
};

function buildRetryDecision(reason: string): CoachLeaderDetailDecision {
  return {
    priority: {
      kind: "retry_required",
      title: "Some coaching context is unavailable",
      reason,
    },
    primaryAction: { kind: "retry", label: "Try again" },
  };
}

function selectRecentContext(
  facts: readonly CoachLeaderDetailRecentContextFact[],
): readonly CoachLeaderDetailRecentContextFact[] {
  return facts
    .filter((fact) => fact.usable && hasValidContextMetadata(fact))
    .map((fact) => ({ ...fact }))
    .sort((left, right) => {
      const byOccurrence = (parseRfc3339Instant(right.occurredAt) ?? 0)
        - (parseRfc3339Instant(left.occurredAt) ?? 0);
      if (byOccurrence !== 0) return byOccurrence;
      return left.canonicalSourceId.localeCompare(right.canonicalSourceId);
    })
    .slice(0, 2);
}

function selectDecision(
  input: CoachLeaderDetailPresentationInput,
  recentContext: readonly CoachLeaderDetailRecentContextFact[],
  options: { readonly allowConfirmedNoContext: boolean },
): CoachLeaderDetailDecision {
  if (input.reviewReadyEvidence?.usable) {
    const { canonicalSourceId, sourceType, occurredAt, title, summary } = input.reviewReadyEvidence;
    const source = { canonicalSourceId, sourceType, occurredAt, title };
    return {
      priority: {
        kind: "review_ready",
        title: "Review the latest coaching evidence",
        reason: summary,
        source,
      },
      primaryAction: {
        kind: "review_evidence",
        label: "Review evidence",
        source,
      },
    };
  }

  if (input.billing.status === "pending_transfer") {
    return {
      priority: {
        kind: "billing_transfer_pending",
        title: "Choose who will manage billing",
        reason: "A billing transfer is waiting for your decision for this leader.",
      },
      primaryAction: {
        kind: "resolve_billing_transfer",
        label: "Resolve billing",
      },
    };
  }

  if (input.upcomingSession) {
    if (input.billing.status === "expired_trial") {
      return {
        priority: {
          kind: "full_access_required",
          title: "Full access is required for new preparation",
          reason: "Existing coaching history remains available. A plan is required for new intelligence.",
        },
        primaryAction: {
          kind: "view_plans",
          label: "View plans",
          reason: "trial_expired",
        },
      };
    }

    const session = { ...input.upcomingSession };
    return {
      priority: {
        kind: "upcoming_session",
        title: `Prepare for ${session.title}`,
        reason: `Scheduled for ${session.startsAt}.`,
        session,
      },
      primaryAction: {
        kind: "prepare_upcoming_session",
        label: "Prepare for next session",
        session,
      },
    };
  }

  if (recentContext.length === 0 && options.allowConfirmedNoContext) {
    if (input.billing.status === "expired_trial") {
      return {
        priority: {
          kind: "full_access_required",
          title: "Full access is required for new preparation",
          reason: "Existing coaching history remains available. A plan is required for new intelligence.",
        },
        primaryAction: {
          kind: "view_plans",
          label: "View plans",
          reason: "trial_expired",
        },
      };
    }

    return {
      priority: {
        kind: "add_context",
        title: "Add current coaching context",
        reason: "No usable meeting or transcript context is available yet.",
      },
      primaryAction: {
        kind: "add_meeting_or_transcript",
        label: "Add a meeting or transcript",
      },
    };
  }

  return { priority: null, primaryAction: null };
}

export function buildCoachLeaderDetailPresentation(
  input: CoachLeaderDetailPresentationInput,
): CoachLeaderDetailPresentation {
  if (input.relationship.status === "checking") {
    return {
      status: "loading",
      scope: "relationship",
      priority: null,
      primaryAction: null,
      recentContext: [],
      unavailableSections: [],
    };
  }

  if (input.relationship.status === "revoked") {
    return {
      status: "relationship_unavailable",
      priority: {
        kind: "relationship_unavailable",
        title: "Leader access is unavailable",
        reason: "The current coaching relationship could not be confirmed.",
      },
      primaryAction: { kind: "back_to_leaders", label: "Back to Leaders" },
      recentContext: [],
      unavailableSections: ["Coaching relationship"],
    };
  }

  if (input.relationship.status === "retryable_error") {
    const decision = buildRetryDecision("Coach Veera could not verify the current coaching relationship.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext: [],
      unavailableSections: ["Coaching relationship"],
    };
  }

  if (!input.sourceReadiness) {
    const decision = buildRetryDecision("Leader Detail readiness could not be verified.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext: [],
      unavailableSections: ["Leader detail"],
    };
  }

  if (input.sourceReadiness.status === "loading") {
    return {
      status: "loading",
      scope: "leader_detail",
      priority: null,
      primaryAction: null,
      recentContext: [],
      unavailableSections: [],
    };
  }

  if (input.sourceReadiness.status === "retryable_error") {
    const decision = buildRetryDecision("One or more coaching-context sources could not be loaded.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext: [],
      unavailableSections: [...input.sourceReadiness.unavailableSections],
    };
  }

  if (input.upcomingSessionReadiness?.status === "loading" && !input.reviewReadyEvidence?.usable) {
    return {
      status: "loading",
      scope: "leader_detail",
      priority: null,
      primaryAction: null,
      recentContext: [],
      unavailableSections: [],
    };
  }

  if (input.upcomingSessionReadiness?.status === "retryable_error" && !input.reviewReadyEvidence?.usable) {
    const decision = buildRetryDecision("Upcoming coaching activity could not be loaded.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext: [],
      unavailableSections: ["Upcoming coaching activity"],
    };
  }

  if (input.reviewReadyEvidence?.usable && !hasValidContextMetadata(input.reviewReadyEvidence)) {
    const decision = buildRetryDecision("Review evidence could not be resolved.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext: [],
      unavailableSections: ["Review evidence"],
    };
  }

  if (input.recentContext.some((fact) => !hasValidContextMetadata(fact))) {
    const decision = buildRetryDecision("Recent coaching context could not be resolved.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext: [],
      unavailableSections: ["Recent coaching context"],
    };
  }

  const recentContext = selectRecentContext(input.recentContext);
  const hasReadableReview = Boolean(input.reviewReadyEvidence?.usable);
  const unavailableSections = [
    ...new Set([
      ...input.sourceReadiness.unavailableSections,
      ...(input.billing.status === "unknown" ? ["Billing"] : []),
    ]),
  ];

  if (input.billing.status === "unknown" && !hasReadableReview) {
    const wouldNeedPremiumAction = Boolean(input.upcomingSession)
      || (recentContext.length === 0 && input.sourceReadiness.status === "ready");
    if (!wouldNeedPremiumAction) {
      return {
        status: "partial",
        priority: null,
        primaryAction: null,
        recentContext,
        unavailableSections,
      };
    }

    const decision = buildRetryDecision("Billing ownership could not be verified for a new premium action.");
    return {
      status: "retryable_error",
      ...decision,
      recentContext,
      unavailableSections,
    };
  }

  const decision = selectDecision(input, recentContext, {
    allowConfirmedNoContext: input.sourceReadiness.status === "ready",
  });
  if (input.sourceReadiness.status === "partial" || input.billing.status === "unknown") {
    return {
      status: "partial",
      ...decision,
      recentContext,
      unavailableSections,
    };
  }

  return {
    status: "ready",
    ...decision,
    recentContext,
    unavailableSections: [],
  };
}

export function adaptLeaderDetailDecisionPresentation({
  decision,
  recentContext,
  reviewReadyEvidence,
  unavailableSections = [],
}: {
  readonly decision: LeaderDetailDecisionPresentation;
  readonly recentContext: readonly CoachLeaderDetailRecentContextFact[];
  readonly reviewReadyEvidence: CoachLeaderDetailRecentContextFact | null;
  readonly unavailableSections?: readonly string[];
}): CoachLeaderDetailPresentation {
  const selectedRecentContext = selectRecentContext(recentContext);

  if (decision.status === "loading") {
    return {
      status: "loading",
      scope: "leader_detail",
      priority: null,
      primaryAction: null,
      recentContext: selectedRecentContext,
      unavailableSections: [],
    };
  }

  if (decision.status === "unavailable") {
    if (decision.reason === "access_unavailable" || decision.reason === "access_error") {
      return {
        status: "relationship_unavailable",
        priority: {
          kind: "relationship_unavailable",
          title: "Leader access is unavailable",
          reason: "The current coaching relationship could not be confirmed.",
        },
        primaryAction: { kind: "back_to_leaders", label: "Back to Leaders" },
        recentContext: [],
        unavailableSections: ["Coaching relationship"],
      };
    }

    return {
      status: "retryable_error",
      priority: {
        kind: "retry_required",
        title: "The next action could not be confirmed.",
        reason: "The next action could not be confirmed from the current coaching sources.",
      },
      primaryAction: { kind: "retry", label: "Try again" },
      recentContext: selectedRecentContext,
      unavailableSections: [...new Set(unavailableSections)],
    };
  }

  const action = decision.primaryAction;
  let mappedDecision: CoachLeaderDetailDecision = { priority: null, primaryAction: null };

  if (action?.kind === "review_transcript" || action?.kind === "review_evidence") {
    const source = reviewReadyEvidence
      ? {
          canonicalSourceId: reviewReadyEvidence.canonicalSourceId,
          sourceType: reviewReadyEvidence.sourceType,
          occurredAt: reviewReadyEvidence.occurredAt,
          title: reviewReadyEvidence.title,
        }
      : null;
    mappedDecision = source
      ? {
          priority: {
            kind: "review_ready",
            title: action.title,
            reason: action.description,
            source,
          },
          primaryAction: {
            kind: "review_evidence",
            label: "Review evidence",
            route: action.route,
            source,
          },
        }
      : buildRetryDecision("Review evidence could not be resolved to an exact source.");
  } else if (action?.kind === "prepare_meeting") {
    const session = {
      canonicalSessionId: action.coachCallId,
      startsAt: action.sourceDate ?? "",
      title: action.title,
    };
    mappedDecision = {
      priority: {
        kind: "upcoming_session",
        title: action.title,
        reason: action.description,
        session,
      },
      primaryAction: {
        kind: "prepare_upcoming_session",
        label: "Prepare for next session",
        route: action.route,
        session,
      },
    };
  } else if (action) {
    mappedDecision = {
      priority: {
        kind: "decision_action",
        title: action.title,
        reason: action.description,
      },
      primaryAction: {
        kind: "follow_decision",
        label: action.label,
        route: action.route,
      },
    };
  }

  return {
    status: unavailableSections.length > 0 ? "partial" : "ready",
    ...mappedDecision,
    recentContext: selectedRecentContext,
    unavailableSections: [...new Set(unavailableSections)],
  };
}
